import type { FormEvent } from "react";
import { NavLink, useLocation } from "react-router-dom-v5-compat";
import { useModuleBEM, IconNameEnum } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { TranslationLanguage } from "@hopper-b2b/types";
import {
  CarLocationAutocomplete,
  CarDateRangePicker,
  useCarSearch,
  getCarRoutes,
} from "@tenant/cars";
import {
  Button,
  ButtonTypeEnum,
  Icon,
  TimePicker,
  InputNumber,
} from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./CarSearch.module.scss";

import { useCarsStyles } from "~core-cars/themes";
import { CoreCarsComponent, DropoffType } from "~core-cars/types";

export interface CarSearchComponentProps extends PlatformComponentProps {
  // Replaces the default onSubmit
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  // Gets called after onSubmit
  onSubmitAppend?: () => void;
}
export const CarSearch = (props: CarSearchComponentProps) => {
  const COMPONENT_KEY = CoreCarsComponent.CarSearch;
  const styles = useCarsStyles(COMPONENT_KEY, defaultStyles);

  const { children, className } = props;
  const { context, handlers } = useCarSearch(props);
  const { BASE_ROUTE: CAR_BASE_ROUTE, FULL_ROUTES: CAR_FULL_ROUTES } =
    getCarRoutes();
  const { pathname } = useLocation();

  const isCarSearchRoute = pathname === CAR_FULL_ROUTES.SEARCH;
  const isCarBaseRoute = pathname === CAR_BASE_ROUTE;

  const { t, language } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();

  const {
    pickupDate,
    pickupTime,
    pickupLocation,
    dropoffDate,
    dropoffTime,
    dropoffLocation,
    dropoffType,
    driverAge,
    formError,
  } = context;
  const {
    handlePickupDateChange,
    handlePickupTimeChange,
    handlePickupLocationChange,
    handleDropoffDateChange,
    handleDropoffTimeChange,
    handleDropoffLocationChange,
    handleDropoffTypeChange,
    handleDriverAgeChange,
    onFormSubmit,
    handlePickupLocationFocus,
    handleDropoffLocationFocus,
  } = handlers;

  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY, {
    ["showDropoffLocation"]: dropoffType === DropoffType.DifferentLocation,
    ["fr"]:
      language === TranslationLanguage["fr-CA"] ||
      language === TranslationLanguage.fr,
  });

  const shouldDisplayDropoffAtPickupOption =
    dropoffType !== DropoffType.SameLocation &&
    pickupLocation &&
    // TODO(RBC-792): fix issue in mobile where autocomplete dialog does not close after clicking button when pickup and dropoff are the same value
    // only displaying option in mobile when the pick-up and drop-off labels in state are different
    (!isDesktopAndUp ? pickupLocation.label !== dropoffLocation?.label : true);

  return (
    <>
      {isCarSearchRoute ? (
        <div className={cn("header")}>
          <h1 className={cn("header-title")}>
            {t("core-cars.search.startSearch")}
          </h1>
          <NavLink className={cn("header-close")} to={CAR_BASE_ROUTE}>
            <Icon iconName={IconNameEnum.close} useCurrentColor={false} />
          </NavLink>
        </div>
      ) : null}
      <form className={clsx(block, className)} onSubmit={onFormSubmit}>
        {isCarBaseRoute || isCarSearchRoute ? (
          <button
            type="button"
            className={cn("drop-off-location-button", {
              show: dropoffType === DropoffType.SameLocation,
            })}
            onClick={() =>
              handleDropoffTypeChange(DropoffType.DifferentLocation)
            }
          >
            <Icon iconName={IconNameEnum.plusCircle} />
            {t("core-cars.search.dropOffAtDifferentLocationButton")}
          </button>
        ) : null}
        <div className={cn("pick-up-location")}>
          <CarLocationAutocomplete
            className={cn("pick-up-location")}
            label={t("core-cars.search.pickup")}
            value={pickupLocation}
            onChange={handlePickupLocationChange}
            onFocus={handlePickupLocationFocus}
            id="pickup-location-autocomplete"
            error={formError.pickupLocation}
            placeholder={t("core-cars.search.placeholders.pickupLocation")}
          />
        </div>
        <div
          className={cn("drop-off-location", {
            show: dropoffType === DropoffType.DifferentLocation,
          })}
        >
          <CarLocationAutocomplete
            key={dropoffLocation?.label}
            label={t("core-cars.search.dropoff")}
            value={
              dropoffType === DropoffType.SameLocation
                ? undefined
                : dropoffLocation
            }
            placeholder={
              dropoffType === DropoffType.SameLocation
                ? t("core-cars.search.dropoffSameLocation")
                : t("core-cars.search.placeholders.dropoffLocation")
            }
            onChange={handleDropoffLocationChange}
            onFocus={handleDropoffLocationFocus}
            id="dropoff-location-autocomplete"
            error={formError.dropoffLocation}
            topContent={
              shouldDisplayDropoffAtPickupOption ? (
                <button
                  type="button"
                  onClick={() => {
                    handleDropoffLocationChange(pickupLocation);
                  }}
                  className={cn("drop-off-at-pickup-button")}
                >
                  <Icon
                    iconName={IconNameEnum.iteration}
                    className={cn("drop-off-at-pickup-icon")}
                  />
                  <div>
                    <p className={cn("drop-off-at-pickup-button-label")}>
                      {t("core-cars.search.dropOffAtPickupLocationButton")}
                    </p>
                    <p className={cn("drop-off-at-pickup-button-location")}>
                      {pickupLocation.label}
                    </p>
                  </div>
                </button>
              ) : null
            }
          />
        </div>
        <div className={cn("date-range")}>
          <CarDateRangePicker
            startDate={pickupDate}
            endDate={dropoffDate}
            onStartDateChangeHandler={handlePickupDateChange}
            onEndDateChangeHandler={handleDropoffDateChange}
            error={formError.pickupDate ?? formError.dropoffDate}
            dialogTitle={t("core-cars.search.date.mobileTitle")}
          />
        </div>
        <TimePicker
          label={t("core-cars.search.pickupTime")}
          value={pickupTime}
          onChange={handlePickupTimeChange}
          className={cn("time-picker", ["pick-up"])}
          steps={30}
          disablePortal={true}
          placeholder={t("core-cars.search.placeholders.pickupTime")}
        />
        <TimePicker
          label={t("core-cars.search.dropoffTime")}
          value={dropoffTime}
          onChange={handleDropoffTimeChange}
          className={clsx(cn("time-picker", ["drop-off"]))}
          steps={30}
          disablePortal={true}
          placeholder={t("core-cars.search.placeholders.dropoffTime")}
        />
        <InputNumber
          className={cn("age")}
          label={t("core-cars.search.driverAge")}
          startContent={<Icon iconName={IconNameEnum.guest} />}
          onChange={(e) => handleDriverAgeChange(e.target.value)}
          value={driverAge ?? ""}
          error={formError.driverAge}
          placeholder={t("core-cars.search.placeholders.driverAge")}
        />
        <Button
          className={cn("search-button")}
          buttonType={ButtonTypeEnum.Primary}
          type="submit"
        >
          {isCarBaseRoute || isCarSearchRoute
            ? t("core-cars.homepage.search.button")
            : t("core-cars.search.searchButton")}
        </Button>
        {children}
      </form>
    </>
  );
};
